import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./styles/Services.css";
import Herobg from "../../assets/images/services/Configuration-Services-Network-Hero.png";

const ArrowIcon = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14M13 5l7 7-7 7" />
  </svg>
);

const ConfigurationServicesDeployment = () => {
  const navigate = useNavigate();

  // Cards (pop-in animation)
  const cardRefs = useRef([]);
  useEffect(() => {
    const els = cardRefs.current.filter(Boolean);
    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in");
            obs.unobserve(entry.target); // animate once
          }
        });
      },
      { threshold: 0.2 }
    );

    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // CTA reveal
  const ctaRef = useRef(null);
  const [ctaInView, setCtaInView] = useState(false);
  useEffect(() => {
    const el = ctaRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setCtaInView(true);
          obs.unobserve(entry.target);
        }
      },
      { threshold: 0.25 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Benefits reveal
  const benefitsRef = useRef(null);
  const [benefitsInView, setBenefitsInView] = useState(false);
  useEffect(() => {
    const el = benefitsRef.current;
    if (!el) return;

    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setBenefitsInView(true);
      },
      { threshold: 0.25 }
    );

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const cards = useMemo(
    () => [
      {
        title: "Device & Endpoint Deployment",
        desc: "Roll out laptops/desktops with standardized builds, policies, and security baselines for consistency.",
      },
      {
        title: "Microsoft 365 / Google Workspace Setup",
        desc: "Configure tenants, domains, email, collaboration apps, and security controls with a clean rollout plan.",
      },
      {
        title: "Network Configuration & Rollouts",
        desc: "Implement reliable switching, Wi‑Fi, VLANs, and firewall policies with documentation and testing.",
      },
      {
        title: "Identity, Access & MFA",
        desc: "Set up users, roles, conditional access, and MFA to reduce risk and improve access control.",
      },
      {
        title: "Software Packaging & Automation",
        desc: "Standardize app installs, updates, and device policies using modern deployment and management tools.",
      },
      {
        title: "Go‑Live Support & Handover",
        desc: "Validate deployments, provide runbooks, and support cutover so teams can adopt new systems smoothly.",
      },
    ],
    []
  );

  return (
    <div className="servicePage">
      {/* HERO — shared markup, identical to every other service page */}
      <section className="hero-section">
        <div className="hero-section__grid-bg" aria-hidden="true" />

        <div className="hero-left">
          <div className="dd-serviceLabel">
            <span className="live-dot" aria-hidden="true" />
            <span>Configuration Services &amp; Deployment</span>
          </div>

          <h1 className="hc-hero__title">
            Streamlined configuration for <br />
            seamless deployments.
          </h1>

          <p className="hc-hero__lead">
            Deploy new technology confidently with consistent configuration,
            security-first policies, and rollout support. Reduce setup time,
            prevent misconfigurations, and deliver a smoother experience for
            end users.
          </p>

          <button
            className="btn-orange"
            type="button"
            onClick={() => navigate("/contact")}
          >
            <span className="btn-text">Meet Our Experts Today</span>
            <ArrowIcon className="btn-arrow" />
          </button>
        </div>

        <div className="hero-right">
          <div className="hero-image-container">
            <img className="hero-img" src={Herobg} alt="Technology deployment" />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* OVERVIEW */}
      <section className="csd-overview">
        <div className="csd-overview__container">
          <div className="csd-overview__grid">
            <div className="csd-overview__left">
              <span className="section-eyebrow">
                <span className="live-dot" aria-hidden="true" />
                Overview
              </span>

              <h2 className="csd-title">
                Standardized deployments that reduce risk and speed up
                delivery
              </h2>

              <p className="csd-text">
                Configuration mistakes are one of the fastest ways to create
                downtime, security gaps, and user frustration. A repeatable
                deployment approach—built on baselines, testing, and
                documentation—keeps rollouts consistent across teams and
                locations.
              </p>

              <h3 className="csd-subtitle">What this service includes</h3>
              <p className="csd-text">
                From endpoint and productivity suites to network and
                identity, deployments are planned, configured, validated, and
                handed over with clear runbooks and support.
              </p>
            </div>

            <div className="csd-overview__right">
              <div className="csd-imageCard">
                <img
                  className="csd-image"
                  src="https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1400&q=80"
                  alt="Configuration planning"
                />
                <span className="scan-corner tl" aria-hidden="true" />
                <span className="scan-corner tr" aria-hidden="true" />
                <span className="scan-corner bl" aria-hidden="true" />
                <span className="scan-corner br" aria-hidden="true" />
              </div>
            </div>

            <div className="csd-overview__below">
              <p className="csd-text csd-text--wide">
                Whether onboarding new employees, opening a new site,
                upgrading infrastructure, or standardizing security policies,
                structured configuration and deployment keeps your
                environment stable and easier to manage long term.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DEPLOYMENT CAPABILITIES (card grid) */}
      <section className="csd-capabilities">
        <div className="csd-capabilities__container">
          <div className="csd-capabilities__grid">
            <div>
              <span className="section-eyebrow">
                <span className="live-dot" aria-hidden="true" />
                Capabilities
              </span>
              <h2 className="csd-cap-title">Deployment capabilities</h2>
              <p className="csd-cap-lead">
                A clear rollout model that covers planning, configuration,
                validation, and support—so technology works on day one and
                stays consistent across teams.
              </p>
            </div>

            <div className="csd-cards">
              {cards.map((c, i) => (
                <div
                  key={c.title}
                  ref={(el) => (cardRefs.current[i] = el)}
                  className="csd-card"
                >
                  <h3 className="csd-cardTitle">{c.title}</h3>
                  <p className="csd-cardText">{c.desc}</p>
                  <button
                    className="csd-cardBtn"
                    type="button"
                    onClick={() => navigate("/contact")}
                  >
                    Learn More
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="csd-cta-section">
        <div ref={ctaRef} className={`csd-cta-card ${ctaInView ? "in" : ""}`}>
          <div className="csd-cta-left">
            <img
              className="csd-cta-illustration"
              src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1400&q=80"
              alt="Deployment support"
            />
            <span className="scan-corner tl" aria-hidden="true" />
            <span className="scan-corner tr" aria-hidden="true" />
            <span className="scan-corner bl" aria-hidden="true" />
            <span className="scan-corner br" aria-hidden="true" />
          </div>

          <div className="csd-cta-right">
            <h2 className="csd-cta-heading">
              Deploy with confidence <br />
              with{" "}
              <span className="csd-cta-heading__accent">ALG Data Guard</span>
            </h2>

            <p className="csd-cta-body">
              Get a reliable rollout plan, secure configurations, and
              practical cutover support so teams stay productive while
              technology changes.
            </p>

            <button
              className="csd-cta-btn"
              type="button"
              onClick={() => navigate("/contact")}
            >
              Talk to an Expert
              <ArrowIcon />
            </button>
          </div>
        </div>
      </section>

      {/* BENEFITS — reuses the shared .benefits-left/.benefits-right system */}
      <section className="csd-benefits-section">
        <div className="csd-benefits-wrap" ref={benefitsRef}>
          <div className={`benefits-left ${benefitsInView ? "in" : ""}`}>
            <h2>Benefits of structured deployments</h2>
            <div className="benefits-underline" />
            <p>
              Consistent configuration improves security posture, reduces
              troubleshooting time, and helps teams scale without chaos.
            </p>
          </div>

          <div className={`benefits-right ${benefitsInView ? "in" : ""}`}>
            <div className="benefit-block">
              <h3>Faster rollout</h3>
              <p>
                Repeatable baselines and checklists reduce setup time and
                errors.
              </p>
            </div>

            <div className="benefit-block">
              <h3>Improved security</h3>
              <p>
                Policies, MFA, and access controls are implemented
                consistently across users and devices.
              </p>
            </div>

            <div className="benefit-block">
              <h3>Cleaner operations</h3>
              <p>
                Documentation and handover make future support simpler and
                more predictable.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ConfigurationServicesDeployment;